import React, { useState } from 'react';
import { isLeaderboardNameLive, useMyLeaderboardName } from '../services/myLeaderboardName';

/**
 * "Your Top Fans name" in the fan's Account tab (Nicky, 27 Oct 2026).
 *
 * Top Fans shows a name built from the fan's email ("Bob R.", "Patriot Mom", or
 * a nickname). This shows it so they recognize themselves on a creator's page,
 * with the one choice a fan gets: their initials instead. No free text, so
 * nothing a fan types can end up on a creator's page.
 *
 * Hidden unless the server says these names are live for this fan
 * (platform_settings.leaderboard_names) and they're signed in.
 */
export default function LeaderboardNameSetting({ userId }: { userId: string | null | undefined }) {
  const { state, setUseInitials } = useMyLeaderboardName(userId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  if (!isLeaderboardNameLive(state)) return null;

  const toggle = async (next: boolean) => {
    setSaving(true);
    setError(false);
    const ok = await setUseInitials(next);
    setSaving(false);
    if (!ok) setError(true);
  };

  const initials = state.initials || null;
  const shown = state.shown || state.name || null;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3" data-testid="leaderboard-name-setting">
      {shown && (
        <div className="text-white text-sm font-semibold">
          Your Top Fans name: <span data-testid="leaderboard-name">{shown}</span>
        </div>
      )}
      <div className="text-xs text-gray-400 mt-1">
        This is how you show up on a creator's Top Fans list when you check in or support them.
        {state.is_nickname && !state.use_initials && ' If another fan on the same list already has this nickname, you get the next one.'}
      </div>
      {initials && (
        <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer mt-3">
          <input
            type="checkbox"
            className="h-4 w-4 accent-blue-600"
            checked={!!state.use_initials}
            disabled={saving}
            onChange={(e) => toggle(e.target.checked)}
          />
          <span>Use my initials instead ({initials})</span>
        </label>
      )}
      {error && <div className="text-xs text-red-400 mt-2">Couldn't save that. Try again.</div>}
    </div>
  );
}
